import type { SalesReportResponse } from "@gioia365/contracts";
import { Fragment, useId, useState, type CSSProperties } from "react";
import { useLocale, useT } from "../i18n/locale";
import { formatCount } from "../i18n/format";
import { gridCellKindOf, gridCellWhatText, hourLabel, hourSpanText, sentenceStart, weekdayText, type GridCellKind } from "./report-format";

// «Quando si chiama» (piano M7, Task 4; mock-up `heatSection` del 17/10/2026; SEZ-4, senza obiettivo minimo): la mappa delle chiamate
// del periodo per giorno della settimana e ora, una riga per giorno della settimana con lavoro (e per sabato e domenica soltanto se
// qualcuno ha chiamato), una colonna per ora dall'apertura alla chiusura. Il numero è scritto dentro ogni cella di lavoro, anche zero;
// il blu ha quattro livelli dal massimo del periodo; pausa e chiuso sono a righe (un motivo, non un colore), con il numero soltanto se
// qualcuno ha chiamato lo stesso. Passando sopra una cella, con il focus o toccandola, la riga sotto (`role="status"`) dice giorno,
// ore, chiamate e risposte; ogni cella ha la stessa frase per lo screen reader. Colonne, righe e stati li manda il server (`callGrid`).
// Ogni colonna è larga almeno quanto il numero più lungo e, sotto 720 px o con il dito, almeno 44 px (UX_SYSTEM.md; ruling del registro
// del Task 4: i 44 px prevalgono su «soltanto «Tutte» scorre»): quando le colonne non ci stanno, la mappa scorre di lato nel suo
// riquadro con i giorni fermi a sinistra, e la pagina mai.

/** Il livello di blu di una cella: 0 senza chiamate o fuori dal lavoro, poi da 1 a 4 in quarti del massimo. */
function levelOf(kind: GridCellKind, calls: number, max: number): number {
  if (kind !== "work" || calls === 0) return 0;
  return Math.max(1, Math.ceil((calls / max) * 4));
}

type Props = {
  report: SalesReportResponse;
};

export function HeatSection({ report }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const titleId = useId();
  // La cella scelta, non la sua frase: se i numeri si rileggono, il dettaglio dice quelli di adesso.
  const [chosen, setChosen] = useState<string | null>(null);
  const grid = report.callGrid;
  if (grid.rows.length === 0 || grid.hours.length === 0) return null;

  const max = Math.max(1, ...grid.rows.flatMap((row) => row.cells.map((cell) => cell.calls)));
  const rows = grid.rows.map((row) => {
    const weekday = weekdayText(t, row.weekday, "long");
    return {
      row,
      label: weekdayText(t, row.weekday, "short"),
      cells: row.cells.map((cell, index) => {
        const kind = gridCellKindOf(grid, row, index);
        const what = gridCellWhatText(t, locale, kind, cell.calls, cell.answered, true);
        return { key: `${row.weekday}:${cell.hour}`, cell, kind, sentence: t("reports.hours.cell", { weekday, span: hourSpanText(t, grid, cell.hour), what }) };
      }),
    };
  });

  // La legenda dice che cosa è a righe: la pausa (dalle sue ore), il venerdì pomeriggio della sequenza, le ore e i giorni chiusi.
  const kinds = new Set(rows.flatMap((row) => row.cells.map((cell) => cell.kind)));
  const pauseHours = rows.flatMap((row) => row.cells.filter((cell) => cell.kind === "pause").map((cell) => cell.cell.hour));
  const parts = [
    ...(pauseHours.length > 0 ? [t("reports.heat.legendPause", { from: String(Math.min(...pauseHours)), to: String(Math.max(...pauseHours) + 1) })] : []),
    ...(kinds.has("friday") ? [t("reports.heat.legendFriday")] : []),
    ...(kinds.has("closed") ? [t("reports.heat.legendClosed")] : []),
  ];
  const legend = parts.length > 0 ? t("reports.heat.legend", { parts: new Intl.ListFormat(locale, { type: "conjunction" }).format(parts) }) : null;
  const picked = rows.flatMap((row) => row.cells).find((cell) => cell.key === chosen);
  const detail = picked === undefined ? t("reports.heat.hint") : t("reports.heat.detail", { sentence: sentenceStart(locale, picked.sentence) });
  // Il numero più lungo della mappa, in caratteri: ogni colonna è larga almeno così (`reports.css`), e nessun numero si taglia.
  const digits = Math.max(1, ...rows.flatMap((row) => row.cells.map(({ cell }) => formatCount(locale, cell.calls).length)));
  const columns = { "--g-report-hours": grid.hours.length, "--g-report-heat-digits": digits } as CSSProperties;

  return (
    <section className="g-report-section g-report-panel" aria-labelledby={titleId}>
      <div className="g-report-head">
        <div>
          <h2 id={titleId}>{t("reports.heat.title")}</h2>
          <p className="g-report-hint">{report.range.from === report.range.to ? t("reports.heat.introDay") : t("reports.heat.introPeriod")}</p>
        </div>
      </div>
      <div className="g-report-heat-frame">
        <div className="g-report-heat" style={columns} role="group" aria-label={t("reports.heat.mapLabel")}>
          <span className="g-report-heat__corner" aria-hidden="true" />
          {grid.hours.map((hour) => (
            <span key={hour} className="g-report-heat__label g-report-heat__hour" aria-hidden="true">
              {hourLabel(grid, hour)}
            </span>
          ))}
          {rows.map(({ row, label, cells }) => (
            <Fragment key={row.weekday}>
              <span className="g-report-heat__label g-report-heat__day" aria-hidden="true">
                {label}
              </span>
              {cells.map(({ key, cell, kind, sentence }) => (
                <button
                  key={key}
                  type="button"
                  className="g-report-heat__cell"
                  data-level={levelOf(kind, cell.calls, max)}
                  data-state={kind === "work" ? undefined : cell.state}
                  aria-label={sentence}
                  onMouseEnter={() => setChosen(key)}
                  onFocus={() => setChosen(key)}
                  onClick={() => setChosen(key)}
                >
                  {kind === "work" || cell.calls > 0 ? formatCount(locale, cell.calls) : null}
                </button>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
      <p className="g-report-detail" role="status">
        {detail}
      </p>
      {legend === null ? null : <p className="g-report-footnote">{legend}</p>}
    </section>
  );
}
